import { useState } from "react";
import { Save } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TextField, AutoTextarea } from "@/components/tools/fields";
import { api, errorText } from "@/lib/api";
import { ErrorText, SelectField, PseudoNotice } from "@/components/rc/shared";

const YEARS = ["", "Nursery", "Reception", ...Array.from({ length: 13 }, (_, i) => `Year ${i + 1}`), "Post-16"];
const NEEDS = ["", "Communication and interaction", "Cognition and learning", "Social, emotional and mental health", "Sensory and/or physical"];
export const STAGES = ["Not started", "Invitations sent", "Reports requested", "Meeting held", "Paperwork sent to LA", "Complete"];
const BLANK = { pupil_ref: "", year_group: "", primary_need: "", review_due: "", last_review: "", stage: "Not started", lead: "", notes: "" };

const Fields = ({ f, set }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    <div className="sm:col-span-2">
      <TextField id="eh-ref" label="Pupil reference code" value={f.pupil_ref} onChange={(v) => set("pupil_ref")(v.toUpperCase())} testid="ehcp-form-ref" placeholder="e.g. P-0423" />
      <p className="mt-1 text-sm text-ink-soft">Letters, numbers and dashes, with at least one number. Never a name or initials.</p>
    </div>
    <SelectField id="eh-year" label="Year group" value={f.year_group} onChange={set("year_group")} options={YEARS} testid="ehcp-form-year" />
    <SelectField id="eh-need" label="Primary area of need" value={f.primary_need} onChange={set("primary_need")} options={NEEDS} testid="ehcp-form-need" />
    <TextField id="eh-due" label="Annual review due" type="date" value={f.review_due} onChange={set("review_due")} testid="ehcp-form-due" />
    <TextField id="eh-last" label="Last review (optional)" type="date" value={f.last_review} onChange={set("last_review")} testid="ehcp-form-last" />
    <SelectField id="eh-stage" label="Review stage" value={f.stage} onChange={set("stage")} options={STAGES} testid="ehcp-form-stage" />
    <TextField id="eh-lead" label="Staff lead (optional)" value={f.lead} onChange={set("lead")} testid="ehcp-form-lead" />
    <div className="sm:col-span-2">
      <AutoTextarea id="eh-notes" label="Notes (no identifying details)" rows={3} value={f.notes} onChange={set("notes")} testid="ehcp-form-notes" />
    </div>
  </div>
);

export const EhcpForm = ({ record, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(() => ({ ...BLANK, ...(record || {}), last_review: record?.last_review || "" }));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.pupil_ref.trim() || !f.review_due) return setError("Please add a pupil reference code and the review due date.");
    setBusy(true);
    setError("");
    const body = { ...f, last_review: f.last_review || null };
    try {
      if (record?.id) await api.put(`/rc/ehcp/${record.id}`, body);
      else await api.post("/rc/ehcp", body);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err).replace("Value error, ", ""));
    } finally {
      setBusy(false);
    }
    return undefined;
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-[2rem] border-2 border-line bg-surface text-ink" data-lenis-prevent data-testid="ehcp-form-dialog">
        <DialogTitle className="font-display text-3xl font-medium">{record?.id ? `Edit ${record.pupil_ref}` : "Add an EHCP review"}</DialogTitle>
        <DialogDescription className="text-ink-soft">Saved straight to your school's workspace.</DialogDescription>
        <form onSubmit={submit} noValidate className="space-y-5">
          <PseudoNotice />
          <Fields f={f} set={set} />
          <ErrorText error={error} testid="ehcp-form-error" />
          <button type="submit" disabled={busy} className="btn btn-brand" data-testid="ehcp-form-submit"><Save size={16} aria-hidden /> {busy ? "Saving…" : "Save review"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
